import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  FileText,
  GitCompareArrows,
  List,
  SlidersHorizontal,
  SquareChartGantt,
  SquareKanban,
  X,
} from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore, useMentionsStore, usePMStore } from "@/stores/pm/hooks/usePMStore";
import * as pmRepo from "@/stores/pm/repository";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import type { UnderlineTabOption } from "@/components/ui/underline-tabs";
import { useMarkProjectActivitySeen } from "@/hooks/projects/useProjectActivity";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { PMEmptyState, PMLoadingState } from "@/domains/pm/components/shared";
import { IssuesList } from "@/domains/pm/components/issues/IssuesList";
import { useProjectResponsibilities } from "@/domains/contacts/useResponsibilities";
import { IssuesToolbar } from "@/domains/pm/components/issues/IssuesToolbar";
import { DEFAULT_ISSUE_SORT } from "@/domains/pm/components/issues/IssueSortControl";
import { IssueDetailPanel, IssueDraftPanel } from "@/domains/pm/components/issues/IssueDetailPanel";
import { ScheduleStagingProvider } from "@/domains/pm/components/issues/schedule/ScheduleStagingProvider";
import { useScheduleConfirm } from "@/domains/pm/components/issues/schedule/useScheduleConfirm";
import { ScheduleChangeDialog } from "@/domains/pm/components/issues/schedule/ScheduleChangeDialog";
import type { ScheduleStagingApi } from "@/domains/pm/components/issues/schedule/scheduleStagingContext";
import { GanttConfirmBar } from "@/domains/pm/components/issues/gantt/GanttConfirmBar";
import { SnapshotBanner } from "@/domains/pm/components/issues/snapshots/SnapshotBanner";
import { SnapshotCompareDialog } from "@/domains/pm/components/issues/snapshots/SnapshotCompareDialog";
import { useStagedSchedule } from "@/domains/pm/hooks/useStagedSchedule";
import {
  issueFiltersStorageKey,
  usePersistedIssueFilters,
} from "@/domains/pm/hooks/usePersistedIssueFilters";
import { selectedIssueParam } from "@/domains/pm/utils/deepLink";
import { useOpenIssueDeepLink } from "@/domains/pm/hooks/useOpenIssueDeepLink";
import type { GanttGroupDefaults } from "@/domains/pm/utils/ganttGroupDefaults";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { IssueFormDialog } from "@/domains/pm/components/issues/IssueFormDialog";
import { IssueBulkActions } from "@/domains/pm/components/issues/IssueBulkActions";
import { ProtocolExportDialog } from "@/domains/pm/components/issues/ProtocolExportDialog";
import { ProtocolSaveCard } from "@/domains/pm/components/issues/protocol/ProtocolSaveCard";
import { useProtocolSave } from "@/domains/pm/components/issues/protocol/useProtocolSave";
import type { ProtocolCardState } from "@/domains/pm/components/issues/protocol/protocolSaveTypes";
import { FolderPickerDialog } from "@/components/chat/SidebarChat/FolderPickerDialog";
import { KanbanBoard } from "@/domains/pm/components/issues/KanbanBoard";
import { GanttView } from "@/domains/pm/components/issues/gantt/GanttView";
import { useDocumentViewer } from "@/domains/documents/hooks/useDocumentViewer";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { ProjectsSection } from "@/domains/documents/components/ProjectsSection";
import type { PMIssue, IssueStatus, ScheduleSnapshot } from "@/domains/pm/types";
import type { IssueGroupBy, IssueSort, IssueSortField } from "@/domains/pm/utils/groupAndSort";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { visibleStatusKeys, visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import { useCustomStatusRow } from "@/domains/pm/statuses/useCustomStatusRow";
import { StatusManagementDialogs } from "@/domains/pm/statuses/StatusManagementDialogs";
import { StatusAdminContext, type StatusAdminApi } from "@/domains/pm/statuses/statusAdminContext";
import { usePermissions } from "@/hooks/usePermissions";
import { overdueDueBefore } from "@/domains/pm/utils/dueDate";
import { toPMIssueFilters, toScopeBaselineFilters } from "@/domains/pm/utils/effectiveIssueFilters";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { usePMIssueLiveUpdates } from "@/domains/pm/hooks/usePMIssueLiveUpdates";
import {
  ScheduleDragPreviewContext,
  createScheduleDragPreviewStore,
} from "@/domains/pm/components/issues/schedule/scheduleDragPreview";
import type { DraftIssueViewProps } from "@/domains/pm/components/issues/issueDraftView";
import { useNewIssueGuard } from "@/domains/pm/useNewIssueGuard";
import { useIssuesPageDraft } from "./useIssuesPageDraft";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/** The three ways of laying the same issues out. The strip that switches
 *  between them sits where the status tabs used to; status is now a filter
 *  dimension, which lets it apply to every view instead of only the list. */
type IssueViewMode = "list" | "kanban" | "gantt";

/**
 * Holds schedule edits back while the Gantt is the active view.
 *
 * A wrapper rather than a prop, because the components that make schedule
 * edits — the bars, the Unscheduled band's pickers, the right-click menu, the
 * detail panel — all reach for the schedule context rather than being told
 * how to save. Wrapping them switches every one of them at once.
 *
 * Staging needs a single project: a batch is applied to one project's
 * schedule, and "All projects" has no single graph to relax.
 */
function MaybeStaged({
  active,
  buildingProjectId,
  buffer,
  staging,
  children,
}: {
  active: boolean;
  buildingProjectId: number | null;
  buffer: ReturnType<typeof useStagedSchedule>;
  staging: ScheduleStagingApi;
  children: React.ReactNode;
}) {
  if (!active || buildingProjectId == null) return <>{children}</>;
  return (
    <ScheduleStagingProvider buffer={buffer} staging={staging}>
      {children}
    </ScheduleStagingProvider>
  );
}

const PMIssues = observer(function PMIssues() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const deepLinkIssueId = selectedIssueParam(searchParams);
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();
  const mentionsStore = useMentionsStore();
  const pmStore = usePMStore();
  const buildingStore = useBuildingStore();
  const { userProfile } = useAuth();
  const orgStore = useOrganizationStore();
  const { currentOrganization } = useOrganizationContext();

  const { projects, isLoading: projectsLoading } = useProjects(id ?? "");
  // The project this page is scoped to. /issues is a Projects-view page:
  // BuildingLayout renders it only once a project is selected, so null is
  // only the transient before the layout settles, which useNewIssueGuard
  // refuses. Drives the issue project filter and where a new issue is filed.
  const { selectedProjectId, projectOptions } = useBuildingScope(id ?? "");
  // "My disciplines": the viewer's Responsibilities in this project (Contacts).
  const { myDisciplineIds } = useProjectResponsibilities(
    id ? Number(id) : null,
    selectedProjectId ?? null
  );
  const { catalogue: statusCatalogue, isLoading: statusCatalogueLoading } = useStatusCatalogue();
  const { canManagePMOnProject } = usePermissions();
  // Status management lives with the page, not inside the filter popover: a
  // status belongs to ONE project, so it can only be offered while a single
  // project is in scope, and keeping the permission read here leaves the
  // popover renderable without a store provider.
  const statusAdmin = useCustomStatusRow(selectedProjectId ?? null);
  const mayManageStatuses = selectedProjectId != null && canManagePMOnProject(selectedProjectId);
  // New Issue files into the scoped project and nowhere else (the form has no
  // project choice), and refuses with a message rather than opening a form the
  // member could not save there.
  const guardCreateIssue = useNewIssueGuard();
  /**
   * "New status" for surfaces that cannot mount their own dialog — the
   * right-click menu and the New Issue form. Held in a ref rather than in
   * state because it is consumed exactly once, by the dialog this call opens,
   * and re-rendering the page on every menu open would close the menu.
   */
  const statusCreatedCallback = useRef<((key: IssueStatus) => void) | undefined>(undefined);
  const statusAdminApi = useMemo<StatusAdminApi | null>(
    () =>
      mayManageStatuses
        ? {
            createStatus: (onCreated) => {
              statusCreatedCallback.current = onCreated;
              statusAdmin.startCreate();
            },
          }
        : null,
    [mayManageStatuses, statusAdmin]
  );
  // The statuses the counts and the overdue query have to know about. Both are
  // scope-wide server counts, so they follow the catalogue rather than the
  // loaded page: a project's own status with no issue on this page still needs
  // a number beside it, and a blank there reads as "none".
  const countedStatusKeys = useMemo(
    () => visibleStatusKeys(statusCatalogue, selectedProjectId ?? null),
    [statusCatalogue, selectedProjectId]
  );
  const openStatusKeys = useMemo(
    () =>
      visibleStatuses(statusCatalogue, selectedProjectId ?? null)
        .filter((d) => d.category === "open")
        .map((d) => d.key),
    [statusCatalogue, selectedProjectId]
  );
  // The issues query depends on projects existing (zero projects shows the
  // "create first project" empty state instead). Gate the load/subscribe
  // effects on this so they don't fire into an empty building.
  const issuesReady = !!id && !projectsLoading && projects.length > 0;
  // Clears this project's Issues dot on the All buildings page.
  useMarkProjectActivitySeen(
    selectedProjectId ?? null,
    "issues",
    issuesReady &&
      issuesStore.currentBuildingId === Number(id) &&
      !issuesStore.isLoading &&
      !issuesStore.error
  );
  // One viewer for the whole page — used by row chips, kanban cards, and
  // the detail panel. Keeps a single DocumentViewerDialog in the tree.
  const viewer = useDocumentViewer();

  const [viewMode, setViewMode] = useState<IssueViewMode>("list");
  const [isProtocolOpen, setIsProtocolOpen] = useState(false);
  /**
   * New issue on this page is a DRAFT: no row exists until it is left with a
   * description (or Create is pressed). It is drawn in the views at once and
   * edited in the issue panel. See useIssuesPageDraft for what leaving does.
   */
  const issueDraft = useIssuesPageDraft({
    buildingId: Number(id),
    orgId: currentOrganization?.id,
    userProfileId: userProfile?.id != null ? Number(userProfile.id) : null,
    selectedProjectId: selectedProjectId ?? null,
    isProtocolOpen,
  });
  const { activeDraft, leaveDraft } = issueDraft;
  /** Closing the draft panel — its X, Escape, or Enter — is leaving the draft. */
  const closeDraftPanel = () => leaveDraft(() => undefined);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [protocolCard, setProtocolCard] = useState<ProtocolCardState | null>(null);
  const [protocolDestinationOpen, setProtocolDestinationOpen] = useState(false);
  const protocolSave = useProtocolSave({
    orgId: currentOrganization?.id,
    buildingId: Number(id),
    userId: userProfile?.id ? Number(userProfile.id) : undefined,
  });

  // The upload's progress belongs on the card. The bytes are released the
  // moment a save succeeds: nothing the `saved` card offers needs them, and
  // holding a whole PDF for a card that has finished its job is a leak.
  useEffect(() => {
    setProtocolCard((prev) =>
      prev
        ? {
            ...prev,
            phase: protocolSave.phase,
            file: protocolSave.phase.phase === "saved" ? null : prev.file,
          }
        : prev
    );
  }, [protocolSave.phase]);

  /**
   * The wizard's embedded detail modal writes the same global `selectedIssue`
   * slot the page panel reads, so the selection is dropped in the SAME commit
   * the gate opens — an effect cleanup would run a frame later and the panel
   * would flash on close.
   *
   * Consequence, deliberately accepted: a panel the user had open before
   * opening the wizard is closed with it. "The wizard owns the selection while
   * it is open" is simpler than snapshot-and-restore for a case nobody hits.
   */
  const handleProtocolOpenChange = (open: boolean) => {
    setIsProtocolOpen(open);
    if (!open) issuesStore.clearSelection();
  };
  // Status the New Issue form opens on. Set by the per-column kanban "add
  // card" so a new issue lands in the clicked column; undefined (the header
  // button / empty state) falls back to the form's "in_progress" default.
  const [formDefaults, setFormDefaults] = useState<GanttGroupDefaults>({});
  const [hasLoaded, setHasLoaded] = useState(false);
  // WHA-3334: bulk-select state. Local to the page since selection is
  // ephemeral (cleared on navigation). Drilled through IssuesList →
  // IssueGroup → IssueRow.
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<number>>(new Set());
  const clearMultiSelect = () => setSelectedIssueIds(new Set());
  const [groupBy, setGroupBy] = useState<IssueGroupBy>(DEFAULT_ISSUE_SORT);
  /**
   * The Gantt keeps its OWN grouping, defaulting to status.
   *
   * A timeline grouped by creation date says nothing — the bars already carry
   * the dates — whereas grouped by status it reads as the phases of the work,
   * which is what a schedule is for. It is separate state rather than a
   * different default for the same one because sharing would mean visiting the
   * Gantt silently re-grouped the List behind you.
   */
  const [ganttGroupBy, setGanttGroupBy] = useState<IssueGroupBy>("status");
  /**
   * The Gantt's unsaved arrangement. Owned here rather than inside the staging
   * provider because the view tabs and the project selector sit ABOVE that
   * provider and have to know whether anything is unsaved before they navigate
   * away from it.
   */
  const stagedBuffer = useStagedSchedule();
  // The Confirm round trip belongs here too, for the same reason: the unsaved
  // bar sits in the view-tabs row, above the staging provider.
  const scheduleConfirm = useScheduleConfirm(Number(id), selectedProjectId ?? null, stagedBuffer);
  const scheduleDirty = viewMode === "gantt" && stagedBuffer.staged.isDirty;
  /**
   * A saved snapshot being read instead of the live schedule, and whether the
   * side-by-side comparison is open. Owned here rather than in the Gantt so
   * that "Compare with today" can sit in the view-tabs row.
   */
  const [snapshot, setSnapshot] = useState<ScheduleSnapshot | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [blockedNavigation, setBlockedNavigation] = useState<(() => void) | null>(null);
  /** Ask before an action that would throw a staged arrangement away. */
  const guardLeave = (action: () => void) => () => {
    if (!scheduleDirty) {
      action();
      return;
    }
    setBlockedNavigation(() => action);
  };
  // Default to newest issue first: issue_number is monotonically increasing,
  // so descending surfaces the most recently created issue at the top of each
  // group without the user having to toggle the sort on every visit.
  const [sort, setSort] = useState<IssueSort>({ field: "issue_number", direction: "desc" });
  // WHA-3423 / WHA-3407: filter selection + debounced search box.
  // Remembered per user, building and project, so leaving the page and coming
  // back (or reloading) keeps the list narrowed the way the user left it. The
  // hook also owns the search debounce, so a project switch swaps the search
  // in the same render as the filters.
  const {
    filter: filterValue,
    setFilter: setFilterValue,
    search: searchInput,
    setSearch: setSearchInput,
    debouncedSearch,
    clearAll: clearFiltersAndSearch,
  } = usePersistedIssueFilters(
    issueFiltersStorageKey("issues", userProfile?.id, id, selectedProjectId),
    {
      knownStatuses: statusCatalogueLoading ? null : countedStatusKeys,
      // A deep link to one issue opens on the whole list, not behind the
      // filters last left here; see the hook.
      deepLink: deepLinkIssueId === null ? null : String(deepLinkIssueId),
    }
  );
  // The ?selected= value already opened: an in-app link to another issue
  // arrives without a remount and must open too.

  // Org users feed the assignee filter picker (WHA-3423).
  const orgUsers = orgStore.users.map((u) => ({
    id: u.id,
    first_name: u.first_name,
    last_name: u.last_name,
    email: u.email,
    avatar_url: u.avatar_url,
  }));

  const handleSortChange = (field: IssueSortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" }
    );
  };

  // Guards the detail panel so a selection carried over from /my-issues (the
  // IssuesStore is a singleton shared across both routes) can't render the
  // panel before this page has cleared it. Without this gate the destination
  // page renders the panel for the stale selection on its very first frame.
  // Flipped true
  // only AFTER the mount clear, so the panel shows only this page's own picks.
  const [selectionReady, setSelectionReady] = useState(false);
  useEffect(() => {
    issuesStore.clearSelection();
    setSelectionReady(true);
    return () => issuesStore.clearSelection();
  }, [issuesStore]);

  // Setup: disciplines and the mentions wiring. Filter application + the issue
  // fetch live in a separate effect below so they re-run on every filter change.
  const userId = userProfile?.id ?? 0;
  useEffect(() => {
    if (!issuesReady || !id) return;
    pmStore.loadDisciplines();
    // WHA-3339: wire the mentions store + initial fetch for the nav dot.
    mentionsStore.setContext(Number(id), userId);
    mentionsStore.refresh();
  }, [id, issuesReady, pmStore, mentionsStore, userId]);

  // Realtime, the chatbot's writes, and cascades: the list, the open issue,
  // the dependency arrows and the row order all follow without a refresh.
  const onDragActiveChange = usePMIssueLiveUpdates(Number(id), {
    enabled: issuesReady && !!id,
    reload: () => void issuesStore.loadIssues(Number(id), userId),
    onChatbotWrite: () => mentionsStore.refresh(),
  });
  // The open issue's panel follows a bar as it is dragged; see scheduleDragPreview.
  const dragPreview = useMemo(() => createScheduleDragPreviewStore(), []);

  // WHA-3423: org users feed the assignee filter picker.
  useEffect(() => {
    if (currentOrganization?.id) {
      orgStore.fetchOrganizationUsers(currentOrganization.id);
    }
  }, [currentOrganization?.id, orgStore]);

  // WHA-3423 / WHA-3407: push the project, filter, and search selections
  // into the store and refetch. Re-runs whenever any of them change.
  //
  // Every dimension — status and overdue included — is applied SERVER-side.
  // That matters most for overdue: the page is capped at PM_PAGE_SIZE_ISSUES
  // rows ordered by issue_number, so filtering it client-side would silently
  // drop overdue issues beyond the cap. The mapping lives in
  // toPMIssueFilters so it can be unit-tested; the baseline it is measured
  // against (project + search, no filter dimensions) is what makes status and
  // overdue count toward "N hidden by filters" now that they are filters
  // rather than tabs.
  useEffect(() => {
    if (!issuesReady || !id) return;
    const numId = Number(id);
    const scope = {
      buildingProjectId: selectedProjectId ?? undefined,
      search: debouncedSearch,
    };
    setHasLoaded(false);
    issuesStore.setFilters(toPMIssueFilters(filterValue, scope), toScopeBaselineFilters(scope));
    issuesStore.loadIssues(numId, userId).finally(() => setHasLoaded(true));
    // Stop the background pager when the scope changes or the page goes away,
    // rather than letting it read on against a list nobody is looking at.
    // Not the view: List, Board and Gantt all lay out the same scope, so
    // switching between them re-read every page of it for nothing.
    return () => issuesStore.cancelLoad();
  }, [id, issuesReady, selectedProjectId, filterValue, debouncedSearch, issuesStore, userId]);

  useOpenIssueDeepLink(deepLinkIssueId, hasLoaded, issuesStore);

  // Overdue badge: a dedicated scope-wide count (building + project, the query
  // form of isIssueOverdue) rather than a scan of the loaded page, so overdue
  // issues beyond the first page are counted whatever the filter says. Mirrors
  // PMMyIssues' dedicated overdue source; like there, it ignores the
  // IssueFilterControl dimensions and search.
  const { data: overdueCount = 0 } = useQuery({
    queryKey: ["pm-overdue-count", id, selectedProjectId, openStatusKeys.join(",")],
    enabled: issuesReady,
    staleTime: 60_000,
    queryFn: () =>
      pmRepo.countOverdueIssues(
        Number(id),
        overdueDueBefore(),
        selectedProjectId ?? undefined,
        // "Overdue" follows the catalogue's categories, so a project's own
        // "Blocked" is counted and its "Cancelled" is not.
        openStatusKeys
      ),
  });

  // Per-status totals for the status filter's rows. These cannot come from the
  // loaded page any more: status is a server-side filter, so a page narrowed
  // to one status can say nothing about the others. Scoped to project +
  // search only, so the numbers hold still while the user ticks boxes.
  const { data: statusCounts } = useQuery({
    queryKey: [
      "pm-status-counts",
      id,
      selectedProjectId,
      debouncedSearch,
      countedStatusKeys.join(","),
    ],
    enabled: issuesReady,
    staleTime: 60_000,
    queryFn: () =>
      pmRepo.countIssuesByStatus(
        Number(id),
        countedStatusKeys,
        selectedProjectId ?? undefined,
        debouncedSearch.trim() || undefined
      ),
  });

  // The view strip sits where the status tabs used to. No counts: all three
  // options describe the same set of issues, so a count on each would be the
  // same number three times. Matches the Documents page's view tabs, which
  // carry icons and no counts.
  const viewTabs: Array<UnderlineTabOption<IssueViewMode>> = [
    { value: "list", label: t.pm.issues.listView, icon: <List /> },
    { value: "kanban", label: t.pm.issues.kanbanView, icon: <SquareKanban /> },
    { value: "gantt", label: t.pm.issues.ganttView, icon: <SquareChartGantt /> },
  ];

  // Opening another issue leaves an open draft first (saved, dropped, or asked about).
  const handleSelectIssue = (issue: PMIssue) => {
    if (activeDraft) {
      leaveDraft(() => void issuesStore.selectIssue(issue.id));
      return;
    }
    if (issuesStore.selectedIssue?.id === issue.id) {
      issuesStore.clearSelection();
    } else {
      issuesStore.selectIssue(issue.id);
    }
  };

  // WHA-3331: jump to a different issue from the detail panel's parent-ref
  // chip — works in both views now that the panel floats over either.
  const handleJumpToIssue = (issueId: number) => issuesStore.selectIssue(issueId);

  /**
   * Every New issue entry point — header button, empty state, a kanban
   * column's "+", a Gantt band's "+" — with whatever that entry point stands
   * for preset. Opens a draft in the panel; while the Gantt shows a SNAPSHOT
   * the draft could not be drawn, so the New issue form opens instead.
   */
  const newIssue = (defaults: GanttGroupDefaults) =>
    guardCreateIssue(selectedProjectId ?? null, () => {
      if (viewMode === "gantt" && snapshot) {
        leaveDraft(() => {
          setFormDefaults(defaults);
          setIsFormDialogOpen(true);
        });
        return;
      }
      // The guard has passed, so a project is in scope and the member may
      // file issues there.
      issueDraft.startDraft({ ...defaults, projectId: selectedProjectId });
    });

  // Header button / empty state: nothing preset. Kanban column: its status.
  // Gantt band: whatever the band stands for.
  const openNewIssueForm = () => newIssue({});
  const handleAddIssueInStatus = (status: IssueStatus) => newIssue({ status });
  const handleAddIssueWithDefaults = (defaults: GanttGroupDefaults) => newIssue(defaults);

  // Search is remembered with the filters now, so clearing has to take it too:
  // otherwise a stale search survives both the click and every reload.
  const clearFilters = clearFiltersAndSearch;

  // Nothing matched, but the scope still holds issues — the filters are what
  // emptied the page. Now that status is filtered SERVER-side that is a
  // routine outcome (tick one status, get no rows), and the generic "no issues
  // yet" state would answer it with a create button: wrong cause, wrong
  // action. Keep the view mounted instead — an emptied kanban column says what
  // the filter did — and name the cause. A truly empty scope still falls
  // through to the generic state, which is then telling the truth.
  // An open draft is a row too: a first issue in an empty building must still
  // appear in the view it was started from.
  const hasVisibleRows = issuesStore.issues.length > 0 || issueDraft.draftRows.length > 0;
  const filtersHideEverything = !hasVisibleRows && issuesStore.hiddenByFilterCount > 0;

  // Drafts are drawn by every view, inert, and hand a click back here.
  const draftViewProps: DraftIssueViewProps = {
    draftIssues: issueDraft.draftRows,
    activeDraftId: activeDraft?.id ?? null,
    savingDraftIds: issueDraft.savingDraftIds,
    onSelectDraft: issueDraft.selectDraft,
  };

  // The list, the board and the timeline, rendered from the same call sites
  // whether or not anything matched.
  const issueViews = (
    <>
      {viewMode === "list" && (
        <IssuesList
          onSelectIssue={handleSelectIssue}
          selectedIssueId={issuesStore.selectedIssue?.id}
          groupBy={groupBy}
          buildingProjectId={selectedProjectId ?? null}
          sort={sort}
          onSortChange={handleSortChange}
          openViewer={viewer.openViewer}
          selectedIssueIds={selectedIssueIds}
          setSelectedIssueIds={setSelectedIssueIds}
          hiddenCount={issuesStore.hiddenByFilterCount}
          onClearFilters={clearFilters}
          {...draftViewProps}
        />
      )}
      {viewMode === "gantt" && (
        <GanttView
          onAddIssue={handleAddIssueWithDefaults}
          snapshot={snapshot}
          onSnapshotChange={setSnapshot}
          buildingId={Number(id)}
          groupBy={ganttGroupBy}
          buildingProjectId={selectedProjectId ?? null}
          selectedIssueId={issuesStore.selectedIssue?.id}
          // A snapshot's rows carry the ids the issues had then. One deleted
          // since has no live row to open, so selecting it would fetch a 404
          // and blank the panel.
          onSelectIssue={(issue) => {
            if (snapshot && !issuesStore.issues.some((i) => i.id === issue.id)) return;
            handleSelectIssue(issue);
          }}
          onDragActiveChange={onDragActiveChange}
          {...draftViewProps}
        />
      )}
      {viewMode === "kanban" && (
        <KanbanBoard
          buildingId={Number(id)}
          openViewer={viewer.openViewer}
          onSelectIssue={handleSelectIssue}
          onAddIssue={handleAddIssueInStatus}
          buildingProjectId={selectedProjectId ?? null}
          onDragActiveChange={onDragActiveChange}
          {...draftViewProps}
        />
      )}
    </>
  );

  const contentState = issuePageContentState({
    projectsLoading,
    projectCount: projects.length,
    buildingId: id,
    hasVisibleRows,
    hasLoaded,
    isLoading: issuesStore.isLoading,
  });
  return (
    <StatusAdminContext.Provider value={statusAdminApi}>
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b px-7 pt-4">
          <div className="mb-3 flex items-start justify-between">
            <div>
              <PageTitle info="issues">{t.pm.issues.title}</PageTitle>
              <PageSubtitle className="mt-1">{t.pm.issues.subtitle}</PageSubtitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                {...pageInfoTarget("issues.protocol")}
                variant="outline"
                size="control"
                // The wizard owns the issue panel while it is open, so it is a leave.
                onClick={() => leaveDraft(() => setIsProtocolOpen(true))}
              >
                <FileText className="h-4 w-4" />
                {t.pm.issues.protocol.button}
              </Button>
              <Button
                size="control"
                onClick={openNewIssueForm}
                {...pageInfoTarget("issues.newIssue")}
              >
                + {t.pm.issues.newIssue}
              </Button>
            </div>
          </div>

          {/* Three bands on one row: the view strip, then whatever the current
            view has to say about its own state, then the toolbar (filter chips
            · Sort · Filter · search). The header spends no band on a row of
            narrow controls, and items-end sits all three on the header rule —
            the strip keeps its own -mb-px so only it crosses, and pb-1.5
            floats the rest clear.

            The middle band is `flex-1` and centres within it: the unsaved-
            changes count is the only sign a drag registered (the cascade is
            not computed until Confirm), so it wants the eye, and the snapshot
            chip sits beside it. A three-column grid would centre it on the
            page exactly, but exact centring and never overlapping are not both
            possible once the toolbar needs more than its share — and printing
            two controls through each other is the worse failure. So the centre
            takes the leftover, yields first, and is an @container so the
            snapshot chip folds to its icon below 480px; the
            toolbar is `shrink-0` and keeps its buttons whole.

            The toolbar stays the flexible half — min-w-0 so its chips collapse
            into "+N more" rather than pushing anything, and its own flex-wrap
            drops the cluster to a second line when even that stops fitting.

            The strip renders in every view. It used to hold the status tabs
            and was hidden in kanban (WHA-3391) because an implicit status axis
            would silently blank a column; it now switches the VIEW, which is
            never something a view should hide. Status moved into the filter
            control, where it is explicit and chip-visible — so it may narrow
            the kanban, and the emptied column stays put to say so. */}
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            {/* -mb-px so the strip's rule lands on the header's own bottom
              border instead of stacking a second line under it. */}
            <UnderlineTabs
              className="-mb-px"
              value={viewMode}
              // Typed arrow rather than `setViewMode` directly: passing the
              // setter makes TS infer the generic as `string` (it takes
              // Dispatch<SetStateAction<T>> as a candidate from a contravariant
              // position), which then rejects the very union it was given.
              // Guarded: leaving the Gantt with an unsaved arrangement asks
              // first, since switching view is the commonest way to lose one.
              onValueChange={(v: IssueViewMode) => guardLeave(() => setViewMode(v))()}
              options={viewTabs}
              infoTarget="issues.views"
            />
            {/* Only the Gantt puts anything here; elsewhere it gives its space to
                the toolbar, where the filter chips need it. */}
            <div
              className={cn(
                "flex min-w-0 items-center justify-center gap-2 pb-1.5 @container",
                viewMode === "gantt" ? "flex-1" : "flex-none"
              )}
            >
              {viewMode === "gantt" && !snapshot && (
                <GanttConfirmBar staging={scheduleConfirm.staging} />
              )}
              {viewMode === "gantt" && snapshot && (
                <SnapshotBanner
                  snapshot={snapshot}
                  onExit={() => {
                    setSnapshot(null);
                    setCompareOpen(false);
                  }}
                />
              )}
              {viewMode === "gantt" && snapshot && (
                <Button
                  variant="outline"
                  size="control"
                  onClick={() => setCompareOpen(true)}
                  className="gap-1.5"
                >
                  <GitCompareArrows className="h-3.5 w-3.5" />
                  {t.pm.issues.gantt.snapshotCompare}
                </Button>
              )}
            </div>
            <IssuesToolbar
              // Takes the row's free space, so the chips have room to sit side by
              // side instead of collapsing into "+N more" against their own width.
              className="min-w-0 flex-[1_1_auto] justify-end pb-1.5"
              users={orgUsers}
              disciplines={pmStore.disciplines}
              filter={{
                value: filterValue,
                onChange: setFilterValue,
                statusCounts,
                overdueCount,
                buildingProjectId: selectedProjectId ?? null,
                onCreateStatus: mayManageStatuses ? statusAdmin.startCreate : undefined,
              }}
              // The kanban's columns already ARE a grouping by status, so a
              // second grouping control there would contradict the board. List
              // and Gantt both group their rows and keep it.
              sort={
                viewMode === "gantt"
                  ? { value: ganttGroupBy, onChange: setGanttGroupBy, show: true }
                  : { value: groupBy, onChange: setGroupBy, show: viewMode !== "kanban" }
              }
              search={{ value: searchInput, onChange: setSearchInput }}
              myDisciplineIds={myDisciplineIds}
            />
          </div>
        </div>

        {/* Filter bar */}

        {/* Content. WHA-3644: the detail panel is now a fixed overlay that
          floats ON TOP of the board/list, so the content keeps its full
          width and never reflows when an issue is selected. */}
        <MaybeStaged
          active={viewMode === "gantt"}
          buildingProjectId={selectedProjectId ?? null}
          buffer={stagedBuffer}
          staging={scheduleConfirm.staging}
        >
          <ScheduleDragPreviewContext.Provider value={dragPreview}>
            <div className="flex flex-1 overflow-hidden">
              {/* No top padding for the Gantt: its axis sits flush under the
                  view tabs' rule, which doubles as the chart's top edge. */}
              <div className={cn("flex-1 overflow-y-auto", viewMode !== "gantt" && "pt-3")}>
                {issuesStore.error && (
                  <div
                    role="alert"
                    className={cn(
                      "mx-7 mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive",
                      viewMode === "gantt" && "mt-3"
                    )}
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{issuesStore.error}</span>
                  </div>
                )}
                {contentState === "loading" ? (
                  <PMLoadingState />
                ) : contentState === "projects" ? (
                  // WHA-3375: when no projects exist, the issues query never fires
                  // (projectFilter stays empty). Show the same "create project"
                  // empty state used at /buildings/:id and the Files page
                  // instead of leaving the page in an infinite spinner.
                  <div className="px-7 pb-6 pt-3">
                    <ProjectsSection
                      buildingId={id}
                      selectedProject={null}
                      onProjectSelect={keepProjectUnselected}
                    />
                  </div>
                ) : contentState === "issues" ? (
                  issueViews
                ) : filterValue.overdueOnly ? (
                  // Overdue filter with nothing overdue — issues exist, none are
                  // late, so the generic "no issues" copy (and its create action)
                  // would mislead.
                  !issuesStore.error && (
                    <PMEmptyState
                      icon={AlertCircle}
                      title={t.pm.issues.emptyOverdue.title}
                      description={t.pm.issues.emptyOverdue.subtitle}
                    />
                  )
                ) : filtersHideEverything ? (
                  <>
                    {/* The list carries this message in its own footer (hiddenCount);
                  the board and the timeline have no footer, so they need the
                  banner — and no view ever shows it twice. The timeline needs
                  it most: without it a filtered-empty Gantt falls through to
                  "no issues have dates yet", which names the wrong cause and
                  offers no way back. */}
                    {viewMode !== "list" && (
                      <div
                        className={cn(
                          "mx-7 mb-3 flex items-center gap-3 rounded-md border border-dashed border-border/60 px-3 py-2 text-xs text-muted-foreground",
                          viewMode === "gantt" && "mt-3"
                        )}
                      >
                        <SlidersHorizontal className="h-4 w-4 flex-shrink-0" />
                        <span>
                          {t.pm.issues.filterBy.hiddenByFilters.replace(
                            "{count}",
                            String(issuesStore.hiddenByFilterCount)
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                        >
                          {t.pm.issues.filterBy.clearFilters}
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {issueViews}
                  </>
                ) : (
                  !issuesStore.error && (
                    <PMEmptyState
                      icon={AlertCircle}
                      title={t.pm.issues.empty}
                      description={t.pm.issues.subtitle}
                      actionLabel={`+ ${t.pm.issues.newIssue}`}
                      onAction={openNewIssueForm}
                    />
                  )
                )}
                {/* Not for the timeline. The reserve exists so a PAGE that scrolls
                  can bring its last row out from under the chat pill; the Gantt
                  scrolls inside itself and fills to the bottom of the column,
                  so reserving here only walled it off behind a 72px dead band.
                  The pill hovers over the chart's own empty corner instead, and
                  both of the chart's axes scroll anything out from under it. */}
                {viewMode !== "gantt" && <ChatLauncherSpacer />}
              </div>

              {/* Detail panel — WHA-3644: a fixed, resizable overlay shared by
            both the list and kanban views. Mounted whenever an issue is
            selected; it layers on top of the content rather than reflowing
            it. */}
              {/* Not while the protocol wizard is open: its embedded detail modal
            writes the same global `selectedIssue` slot this reads, so the
            panel would pop up behind the modal. */}
              {selectionReady && !isProtocolOpen && activeDraft && (
                <IssueDraftPanel
                  // One panel per draft: a new draft starts with fresh field state.
                  key={activeDraft.id}
                  buildingId={Number(id)}
                  values={activeDraft.values}
                  onChange={(patch, meta) => issuesStore.updateDraft(patch, activeDraft.id, meta)}
                  onUploadingChange={(uploading) =>
                    issuesStore.setDraftUploading(activeDraft.id, uploading)
                  }
                  onClose={closeDraftPanel}
                  // Enter is the same way out: a titled draft is saved, an
                  // untouched one dropped, input with no description asked about.
                  onEnterSave={closeDraftPanel}
                  titleFocusToken={issueDraft.titleFocusToken}
                  error={issueDraft.draftError}
                  headerActions={
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-3 text-xs"
                      onClick={issueDraft.createDraft}
                      disabled={activeDraft.uploading}
                    >
                      {t.pm.issues.draft.create}
                    </Button>
                  }
                />
              )}
              {selectionReady && !isProtocolOpen && !activeDraft && issuesStore.selectedIssue && (
                <IssueDetailPanel
                  // While a snapshot is open the chart draws the schedule as it
                  // WAS, but the panel fetches the live row — so an editable
                  // panel beside it would both contradict the chart and invite a
                  // write nobody meant to make.
                  readOnly={viewMode === "gantt" && snapshot != null}
                  issue={issuesStore.selectedIssue}
                  onClose={() => issuesStore.clearSelection()}
                  openViewer={viewer.openViewer}
                  onJumpToIssue={handleJumpToIssue}
                />
              )}
            </div>
          </ScheduleDragPreviewContext.Provider>
        </MaybeStaged>

        {/* Shared document preview dialog */}
        <DocumentViewerDialog
          document={viewer.document}
          blobUrl={viewer.blobUrl}
          blobData={viewer.blobData}
          open={viewer.isOpen}
          onOpenChange={viewer.setIsOpen}
          originalDwgFilePath={viewer.originalDwgFilePath ?? undefined}
        />

        {id && protocolCard && (
          <>
            <ProtocolSaveCard
              phase={protocolCard.phase}
              filename={protocolCard.filename}
              blockedReason={
                !currentOrganization?.id || !userProfile?.id
                  ? t.pm.issues.protocol.save.unavailable
                  : undefined
              }
              copy={t.pm.issues.protocol}
              onSave={() => setProtocolDestinationOpen(true)}
              onDismiss={() => setProtocolCard(null)}
            />
            {protocolDestinationOpen && protocolCard.file && (
              <FolderPickerDialog
                open
                onOpenChange={(next) => {
                  if (!next) setProtocolDestinationOpen(false);
                }}
                buildingId={id}
                initialProjectId={selectedProjectId != null ? String(selectedProjectId) : undefined}
                filename={protocolCard.filename}
                // Its defaults are hard-coded English — leaving them unset ships
                // English strings into the Estonian UI.
                title={t.pm.issues.protocol.save.destinationTitle}
                description={t.pm.issues.protocol.save.destinationDescription}
                submitLabel={t.pm.issues.protocol.save.destinationSubmit}
                onSelect={(picked) => {
                  const file = protocolCard.file;
                  setProtocolDestinationOpen(false);
                  if (file) void protocolSave.save(file, picked);
                }}
              />
            )}
          </>
        )}

        {id && (
          <ProtocolExportDialog
            open={isProtocolOpen}
            onOpenChange={handleProtocolOpenChange}
            openViewer={viewer.openViewer}
            onCreated={(created) => setProtocolCard({ ...created, phase: { phase: "created" } })}
            buildingId={Number(id)}
            // Null while the building detail is still loading. Falling back to
            // the organisation name (or a literal "Franklin") would print a
            // header that confidently names the wrong entity.
            buildingName={buildingStore.getById(id)?.base.name ?? null}
            buildingProjectId={selectedProjectId ?? undefined}
            // Undefined on a deep link whose project options haven't loaded yet.
            // The id above is what tells the document the export IS project
            // scoped, so an unresolved name reads as unknown rather than as
            // "All projects" — which the filtered query would make a lie.
            projectName={projectOptions.find((project) => project.id === selectedProjectId)?.name}
          />
        )}

        {id && (
          <IssueFormDialog
            open={isFormDialogOpen}
            onOpenChange={setIsFormDialogOpen}
            buildingId={Number(id)}
            defaultProjectId={selectedProjectId != null ? String(selectedProjectId) : undefined}
            defaultStatus={formDefaults.status}
            defaultAssigneeIds={formDefaults.assigneeIds}
            defaultDisciplineIds={formDefaults.disciplineIds}
          />
        )}

        {/* The snapshot and today, side by side. Mounted on the page because
          the button that opens it is in the view-tabs row. */}
        {snapshot && selectedProjectId != null && (
          <SnapshotCompareDialog
            open={compareOpen}
            onOpenChange={setCompareOpen}
            snapshot={snapshot}
            selectedIssueId={issuesStore.selectedIssue?.id}
            // The comparison names issues the store may not hold — one gone
            // since the snapshot has no live row to select.
            onSelectIssue={(issueId) => {
              const target = issuesStore.issues.find((i) => i.id === issueId);
              if (target) handleSelectIssue(target);
            }}
          />
        )}

        {/* The cascade Confirm predicts, awaiting the user's decision. Mounted
          here, beside the buffer it settles, rather than inside the staging
          provider. */}
        <ScheduleChangeDialog
          pending={
            scheduleConfirm.pending
              ? {
                  issue: null,
                  start: null,
                  due: null,
                  preview: scheduleConfirm.pending,
                  batch: true,
                }
              : null
          }
          applying={scheduleConfirm.staging.saving}
          onResolve={scheduleConfirm.resolve}
          onCancel={scheduleConfirm.cancel}
        />

        {/* Leaving the Gantt with schedule changes that were never confirmed. */}
        <AlertDialog
          open={blockedNavigation != null}
          onOpenChange={(open) => !open && setBlockedNavigation(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.pm.issues.gantt.leaveTitle}</AlertDialogTitle>
              <AlertDialogDescription>{t.pm.issues.gantt.leaveBody}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setBlockedNavigation(null)}>
                {t.pm.issues.gantt.leaveStay}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive hover:bg-destructive/90"
                onClick={() => {
                  const go = blockedNavigation;
                  stagedBuffer.discard();
                  setBlockedNavigation(null);
                  go?.();
                }}
              >
                {t.pm.issues.gantt.leaveDiscard}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Leaving a draft that has input but no description to save it under. */}
        <AlertDialog
          open={issueDraft.warning.open}
          onOpenChange={(open) => !open && issueDraft.warning.addDescription()}
        >
          {/* Focus goes to the description, not back to whatever opened this. */}
          <AlertDialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.pm.issues.draft.needsDescriptionTitle}</AlertDialogTitle>
              <AlertDialogDescription>
                {t.pm.issues.draft.needsDescriptionBody}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={issueDraft.warning.addDescription}>
                {t.pm.issues.draft.addDescription}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive hover:bg-destructive/90"
                onClick={issueDraft.warning.discard}
              >
                {t.pm.issues.draft.discard}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create / edit / delete a project status. Mounted at page level so
          the filter popover, the right-click menu and the New Issue form can
          all close behind their own dialog. */}
        <StatusManagementDialogs
          admin={statusAdmin}
          onCreated={(key) => {
            const cb = statusCreatedCallback.current;
            statusCreatedCallback.current = undefined;
            cb?.(key);
          }}
        />

        {/* Floating bulk-action toolbar — only visible when ≥1 row checked. */}
        {id && viewMode === "list" && (
          <IssueBulkActions
            selectedIssueIds={Array.from(selectedIssueIds)}
            buildingId={Number(id)}
            onClear={clearMultiSelect}
          />
        )}
      </div>
    </StatusAdminContext.Provider>
  );
});

export default PMIssues;

// No project is selected until creation refetches the list and leaves the empty state.
function keepProjectUnselected() {
  return undefined;
}

interface IssuePageContentInput {
  projectsLoading: boolean;
  projectCount: number;
  buildingId: string | undefined;
  hasVisibleRows: boolean;
  hasLoaded: boolean;
  isLoading: boolean;
}
function issuePageContentState(input: IssuePageContentInput) {
  if (input.projectsLoading) return "loading";
  if (input.projectCount === 0 && input.buildingId) return "projects";
  if (input.hasVisibleRows) return "issues";
  if (!input.hasLoaded || input.isLoading) return "loading";
  return "empty";
}
